import deepEqual from "deep-equal";

import { definedMap } from "@glideapps/ts-necessities";

import { makeTableName } from "../common/description";
import { panic } from "../support";
import { logError } from "../common/debug-print";
import type { ValueListener } from "../common/components/computation-types";
import { isObjectValue, LOADING } from "../common/components/computation-types";
import type {
    ValueProducer,
    PotentialValue,
    AppEnvironment,
    Data,
    Value,
    ObjectValue,
    AppOther,
    ForceValueProducer,
    Loading,
    ObjectInspector,
} from "../common/components/types";
import type { KeyRefObject } from "../common/components/data";
import {
    refKeyComponents,
    isRefObject,
    isRefMatch,
    getRefObjectTable,
    isKeyRefObject,
} from "../common/components/data";
import type { SettableForwardingValueProducer } from "../common/components/component-helpers";
import {
    isLoadedDefined,
    makeObjectInspector,
    SubscribableValueProducer,
} from "../common/components/component-helpers";
import type { TableName } from "../common/database-strings";
import { documentIDForTableName } from "../common/database-strings";
import type { DataForTables, TableFetcher } from "./types";

export class RefValueProducer
    extends SubscribableValueProducer<AppOther, AppEnvironment>
    implements ValueListener<AppOther, AppEnvironment>
{
    /*
     * States we have to keep track of:
     *
     * 1. Not computed yet.
     * 2. Computed, but `LOADING` or `undefined`.
     * 3. Computed with a proper value.
     *
     * Transitions:
     *
     * From 1:
     *   + update with listeners -> 2 or 3
     * From 2:
     *   + update with listeners -> 2 or 3
     *   + update without listeners -> 1
     * From 3:
     *   + update with listeners -> 2 or 3
     *   + update without listeners -> 1
     *
     * We represent the states as such:
     *
     * 1. _isComputed === false, _current === undefined
     * 2. _isComputed === true, _current === LOADING | undefined
     * 3. _isComputed === true, _current === value
     */
    private _isComputed: boolean = false;
    private _current: PotentialValue = undefined;
    private _currentObject: ValueProducer | undefined;

    constructor(
        private readonly _dataForTables: DataForTables,
        private readonly _ref: ValueProducer,
        private readonly _fetcher: TableFetcher
    ) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();
        this._ref.subscribe(this);
        if (this._currentObject !== undefined) {
            this._currentObject.subscribe(this);
        }
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this._ref.unsubscribe(this);
        if (this._currentObject !== undefined) {
            this._currentObject.unsubscribe(this);
        }
    }

    private setComputed(isComputed: boolean): void {
        this._isComputed = isComputed;
        this._current = undefined;
        if (this._currentObject !== undefined && this.hasSubscribers) {
            this._currentObject.unsubscribe(this);
        }
        this._currentObject = undefined;
    }

    private getTableData(
        withFetch: boolean
    ):
        | { refObject: KeyRefObject; tableData: Data | SettableForwardingValueProducer<AppOther, AppEnvironment> }
        | undefined {
        const refObject = this._ref.current;
        if (!isLoadedDefined(refObject)) {
            this._current = refObject;
            return undefined;
        }

        if (!isKeyRefObject(refObject)) {
            logError("Link is not a ref object", refObject);
            return undefined;
        }

        const tableName: TableName | undefined = definedMap(getRefObjectTable(refObject), makeTableName);
        if (tableName === undefined) return undefined;

        if (withFetch) {
            this._fetcher.fetchTableRows(tableName);
        }

        const documentID = documentIDForTableName(tableName);
        const tableData = this._dataForTables[documentID];
        if (tableData === undefined) {
            logError("Table not found for ref", refObject);
            return undefined;
        }

        return { refObject, tableData };
    }

    private computeRef(): void {
        this.setComputed(true);

        const maybeTableData = this.getTableData(true);
        if (maybeTableData === undefined) return;
        const { refObject, tableData } = maybeTableData;

        const { $ref, $key, $multiple } = refObject;
        const keyComponents = refKeyComponents($key);

        function followValue(obj: ObjectValue): Value | null {
            if (keyComponents.length === 0) return null;

            const vp = obj[keyComponents[0]];
            if (vp === undefined) return null;

            const { current } = vp;
            if (current === LOADING) return null;

            if (keyComponents.length > 1) {
                if (!isRefObject(current) || !isKeyRefObject(current)) return null;

                if (!deepEqual(refKeyComponents(current.$key), keyComponents.slice(1), { strict: true })) {
                    logError("Deep keys not equal", $ref, $key, obj);
                    return null;
                }

                const value = current.$value;
                if (value === undefined) {
                    return null;
                }
                return value as Value;
            }

            return current;
        }

        const tableRows = tableData.current;
        if (tableRows === LOADING) {
            this._current = LOADING;
            return;
        }

        if (!Array.isArray(tableRows)) {
            logError("Table rows is not an array");
            return;
        }

        const matches = tableRows.filter(row => {
            const rowValue = row.current;
            if (!isObjectValue<AppOther, AppEnvironment>(rowValue)) return false;

            const followed = followValue(rowValue);
            if (followed === null) return false;

            return isRefMatch(followed, rowValue, refObject);
        });

        // FIXME: Check whether the result has been updated.  If not, just set it
        // back to the old value and return `false`.
        if ($multiple) {
            this._current = matches;
        } else {
            if (matches.length === 0) {
                // logError("Could not find target for ref", refObject);
                return;
            }

            const matchVP = matches[0];
            const match = matchVP.current;
            if (match === LOADING) {
                logError("Match is loading");
                return;
            }

            this._current = match;
            this._currentObject = matchVP;
            if (this.hasSubscribers) {
                this._currentObject.subscribe(this);
            }
        }
    }

    private computeRefIfNecessary(): void {
        if (this._isComputed) return;
        this.computeRef();
    }

    public setDirty(): void {
        this.setComputed(false);
        this.setListenersDirty();
    }

    public get current(): PotentialValue {
        this.computeRefIfNecessary();
        return this._current;
    }

    public set(_v: Value): boolean {
        return panic("Cannot set a RefValueProducer");
    }

    public addProperty(name: string, vp: ValueProducer): void {
        this.computeRefIfNecessary();

        if (this._currentObject === undefined) {
            return panic("Cannot add property to non-object");
        }

        this._currentObject.addProperty(name, vp);
    }

    public removeProperty(name: string): void {
        this.computeRefIfNecessary();

        if (this._currentObject === undefined) {
            return panic("Cannot remove property from non-object");
        }

        this._currentObject.removeProperty(name);
    }

    public appendItem(vp: ValueProducer): void {
        const tableData = this.getTableData(false);
        if (tableData === undefined) {
            logError("Trying to add item to an incomplete RefValueProducer");
            return;
        }

        tableData.tableData.appendItem(vp);
    }

    public removeLastItem(): void {
        return panic("Cannot remove an item from a RefValueProducer");
    }

    public removeItemAtIndex(_index: number): void {
        return panic("Cannot remove an item from a RefValueProducer");
    }

    public removeItem(_item: ValueProducer): void {
        return panic("Cannot remove an item from a RefValueProducer");
    }

    public removeFromParent(): void {
        return panic("Cannot remove a RefValueProducer from its parent");
    }

    public getObjectInspector(force: ForceValueProducer | undefined): ObjectInspector | Loading | undefined {
        return makeObjectInspector(this, force);
    }
}
